import {
  requestMapRecenter,
  setSelectedDeviceId,
} from 'features/Integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/Integrations/state/thunks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { bindTooltipCanvasClick } from '../utils/graphInteraction.utils';
import { buildUSGSDeviceChartModel, getUSGSDevices } from '../utils/usgsDeviceGraph.utils';
import * as usgsEventUtils from '../utils/usgsEvent.utils';
import {
  buildAlignedDatasets,
  buildChartOptionFromModel,
  buildSeriesFromDatasets,
  buildUSGSTooltipFormatter,
  buildXAxisLabelFormatter,
  computeUnitDisplay,
  computeYAxisLabel,
} from '../utils/usgsGraphDisplay.utils';

export const useUsgsGraphBuilder = ({
  title = 'Devices',
  devices = [],
  selectedDeviceMap = {},
  selectedDataType,
  unitType,
  setUnitType,
  startTime = undefined,
  endTime = undefined,
  dispatch = undefined,
  navigate = undefined,
  location = undefined,
  onSelectedDeviceIndex = undefined,
  onGraphDatasheetSelect = undefined,
  setShowCurrentDevice = undefined,
  setShowProvisional = undefined,
}) => {
  const tooltipParamsRef = useRef(null);
  const zrenderCleanupRef = useRef(null);
  const selectGraphDataPointRef = useRef(null);

  useEffect(
    () => () => {
      zrenderCleanupRef.current?.();
      zrenderCleanupRef.current = null;
    },
    [],
  );

  const model = useMemo(
    () =>
      buildUSGSDeviceChartModel({
        devices,
        selectedDeviceMap,
        selectedDataType,
        unitType,
        startTime,
        endTime,
      }),
    [devices, selectedDeviceMap, selectedDataType, unitType, startTime, endTime],
  );

  const { labels, datasets } = model || { labels: [], datasets: [] };
  const hasData = Array.isArray(datasets) && datasets.length > 0;
  const alignedDatasets = useMemo(() => buildAlignedDatasets(datasets), [datasets]);

  useEffect(() => {
    tooltipParamsRef.current = null;
  }, [alignedDatasets]);

  const hasSelectedDevice = useMemo(
    () => Object.keys(selectedDeviceMap || {}).some((key) => selectedDeviceMap[key]),
    [selectedDeviceMap],
  );

  const fallbackDevice = useMemo(() => {
    if (!hasSelectedDevice) return null;

    const firstSelectedId = Object.keys(selectedDeviceMap || {}).find(
      (id) =>
        selectedDeviceMap[id] && getUSGSDevices(devices).some((device) => String(device.id) === id),
    );
    return firstSelectedId
      ? (devices.find((device) => String(device.id) === firstSelectedId) ?? null)
      : null;
  }, [devices, selectedDeviceMap, hasSelectedDevice]);

  const series = useMemo(() => {
    if (!hasData) return [];
    return buildSeriesFromDatasets(alignedDatasets, selectedDataType);
  }, [hasData, alignedDatasets, selectedDataType]);

  const tooltipFormatter = useMemo(() => {
    if (!hasData) return null;
    const formatTooltip = buildUSGSTooltipFormatter(alignedDatasets, unitType);
    return (params) => {
      tooltipParamsRef.current = params;
      return formatTooltip(params);
    };
  }, [hasData, alignedDatasets, unitType]);

  const xAxisLabelFormatter = useMemo(() => buildXAxisLabelFormatter(), []);
  const yAxisLabel = useMemo(
    () => computeYAxisLabel(datasets, selectedDataType, unitType),
    [datasets, selectedDataType, unitType],
  );
  const unitDisplay = useMemo(
    () => computeUnitDisplay(datasets, selectedDataType, unitType),
    [datasets, selectedDataType, unitType],
  );

  const option = useMemo(() => {
    if (!hasData) return null;
    return buildChartOptionFromModel({
      title,
      labels,
      startTime,
      endTime,
      series,
      tooltipFormatter,
      xAxisLabelFormatter,
      yAxisLabel,
    });
  }, [
    hasData,
    title,
    labels,
    startTime,
    endTime,
    series,
    tooltipFormatter,
    xAxisLabelFormatter,
    yAxisLabel,
  ]);

  const selectGraphDataPoint = useCallback(
    (params) => {
      if (!params) return;

      const seriesIndex = params.seriesIndex ?? 0;
      const dataset = alignedDatasets[seriesIndex] || datasets[seriesIndex] || {};
      const datasetMeta = dataset?._meta || {};
      const deviceId = datasetMeta?.id ?? null;

      if (deviceId && Array.isArray(devices)) {
        const globalIndex = (devices || []).findIndex((device) => device.id === deviceId);
        if (globalIndex >= 0) {
          onSelectedDeviceIndex?.(globalIndex);
          setShowCurrentDevice?.(true);
        }
      }

      const dataRowId = usgsEventUtils.resolveDataRowIdFromParams(params, datasetMeta);
      if (!deviceId) return;
      if (dataRowId == null) return;

      try {
        usgsEventUtils.setUidDidInUrl(deviceId, dataRowId, navigate, location);
      } catch (e) {
        // URL updates are non-critical for graph selection.
      }

      onGraphDatasheetSelect?.(deviceId);
      dispatch?.(setSelectedDeviceId(deviceId));
      dispatch?.(requestMapRecenter());
      dispatch?.(setSelectedDevice({ id: deviceId }));
    },
    [
      alignedDatasets,
      datasets,
      devices,
      onSelectedDeviceIndex,
      onGraphDatasheetSelect,
      setShowCurrentDevice,
      navigate,
      location,
      dispatch,
    ],
  );

  useEffect(() => {
    selectGraphDataPointRef.current = selectGraphDataPoint;
  }, [selectGraphDataPoint]);

  const onChartClick = useMemo(
    () => (params) => {
      if (!params || params.componentType !== 'series') return;
      selectGraphDataPoint(params);
    },
    [selectGraphDataPoint],
  );

  const onChartReady = useMemo(
    () => (chartInstance) => {
      zrenderCleanupRef.current?.();
      zrenderCleanupRef.current = bindTooltipCanvasClick(
        chartInstance,
        () => tooltipParamsRef.current,
        (tooltipParam) => selectGraphDataPointRef.current?.(tooltipParam),
      );
    },
    [],
  );

  if (!option) {
    if (fallbackDevice) {
      return { option: null, fallbackDatasheetDevice: fallbackDevice };
    }

    return {
      option: null,
      emptyMessage: hasSelectedDevice ? 'No Data in time range' : 'No device selected',
    };
  }

  return {
    option,
    datasets,
    onChartClick,
    onChartReady,
    controlsLeft: [
      {
        key: 'units',
        label: `Units: ${unitDisplay ?? (unitType ? 'Metric' : 'Imperial')}`,
        className: 'm-2 px-2 py-1',
        onClick: () => setUnitType?.((prev) => !prev),
      },
    ],
    controlsRight: [
      {
        key: 'provisional-info',
        icon: <IoMdInformationCircleOutline size={18} />,
        ariaLabel: 'Show USGS provisional disclaimer',
        title: 'USGS provisional disclaimer',
        className: 'm-1 px-2 py-1 d-inline-flex align-items-center justify-content-center lh-1',
        onClick: () => setShowProvisional?.(true),
      },
    ],
  };
};
